/**
 * Static content data for all service category info pages.
 * Each key matches a Sharetribe Console category ID.
 */

export const SERVICE_INFO_DATA = {
  tree_service: {
    title: 'Professional Tree Trimming & Removal',
    subtitle:
      'Connect with local tree care professionals for safe removals, expert pruning, and fire fuel reduction.',
    colors: ['#1a5d2a', '#2d8a4e'],
    services: [
      {
        icon: '\u{1F332}',
        title: 'Tree Removal',
        description:
          'Safe removal of dead, dying, or hazardous trees from your property by licensed professionals.',
      },
      {
        icon: '\u2702\uFE0F',
        title: 'Trimming & Pruning',
        description:
          'Crown thinning, dead-branch removal, and shaping to promote healthy growth and reduce fire risk.',
      },
      {
        icon: '\u{1F525}',
        title: 'Fire Fuel Reduction',
        description:
          'Strategic thinning and limbing to create defensible space and meet California PRC 4291 requirements.',
      },
      {
        icon: '\u{1F69C}',
        title: 'Lot Clearing',
        description:
          'Full lot or parcel clearing for new construction, fire breaks, or property improvement projects.',
      },
      {
        icon: '\u26A1',
        title: 'Emergency Storm Damage',
        description:
          '24/7 response for fallen trees, broken limbs, and storm debris blocking roads or threatening structures.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Tree has fallen on a structure or vehicle',
          'Broken limbs hanging over a roof or power line',
          'Storm damage blocking a road or driveway',
          'Tree is actively leaning and could fall imminently',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Large dead branches in the canopy',
          'Trunk cracks, cavities, or fungal growth',
          'Roots lifting sidewalks or foundations',
          'Trees within 5 feet of structures (Zone 0)',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Annual pruning and crown maintenance',
          'Defensible space zone clearing before fire season',
          'Stump grinding after a previous removal',
          'Lot clearing for a future construction project',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Count your trees',
        description:
          'Walk your property and note how many trees need work. Contractors price per tree, so an accurate count helps you get precise quotes.',
      },
      {
        number: 2,
        title: 'Estimate sizes',
        description:
          'Measure the trunk diameter at chest height (DBH). A rough estimate is fine \u2014 small (under 12\u201D), medium (12-24\u201D), or large (over 24\u201D).',
      },
      {
        number: 3,
        title: 'Identify the species',
        description:
          "Pine, oak, cedar, and eucalyptus each require different techniques. Snap a photo of the leaves or bark if you're unsure.",
      },
      {
        number: 4,
        title: 'Note access limitations',
        description:
          'Can a truck and chipper reach the trees? Mention steep slopes, narrow gates, overhead wires, or septic systems nearby.',
      },
      {
        number: 5,
        title: 'Take photos',
        description:
          'Upload clear photos showing each tree, the surrounding area, and any hazards. Photos help contractors quote more accurately.',
      },
      {
        number: 6,
        title: 'Mention debris preferences',
        description:
          'Do you want logs left for firewood? Chips spread as mulch? Everything hauled away? Let contractors know so they can quote accordingly.',
      },
    ],
    faq: [
      {
        q: 'How much does tree removal cost?',
        a: 'Cost depends mostly on tree size and species, how close the tree is to structures or power lines, access for equipment, and whether the wood is chipped on-site or hauled away. Large trees near a house cost the most. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'Do I need a permit to remove a tree?',
        a: "It depends on your county and city. Many California jurisdictions require permits for removing trees above a certain size, especially oaks and other protected species. Your contractor can usually advise on local requirements, and some will handle the permit process for you.",
      },
      {
        q: 'When is the best time of year for tree work?',
        a: "Late fall through early spring (November\u2013March) is generally ideal \u2014 trees are dormant, there's less risk of pest spread, and many contractors have more availability. However, hazardous trees and fire fuel reduction should be addressed as soon as possible regardless of season.",
      },
      {
        q: 'How does tree work relate to fire safety?',
        a: "Trees are a primary fuel source in wildfires. California's PRC 4291 requires removing dead trees and vegetation within 100 feet of structures. Proper pruning (limbing up to 6\u201310 ft), crown spacing, and removing ladder fuels dramatically improve your home's survivability.",
      },
      {
        q: 'What should I expect on the day of service?',
        a: "The crew will arrive with trucks, chippers, and climbing gear or a bucket truck. They'll review the scope with you, set up safety zones, and begin work. Most residential jobs take half a day to a full day. You don't need to be home the entire time, but should be available at the start.",
      },
      {
        q: 'Should I remove the stump too?',
        a: 'Stump grinding is recommended for aesthetics, safety (trip hazard), and to prevent regrowth or pest infestation. Stumps can be ground 6\u201312 inches below grade, and the grindings make great mulch.',
      },
      {
        q: 'How do I know if a tree is hazardous?',
        a: 'Warning signs include: dead or hanging branches, trunk cracks or cavities, mushrooms or fungal growth at the base, significant lean (especially if new), root heaving, and bark falling off. If you see any of these signs, request a professional assessment promptly.',
      },
      {
        q: 'How can I check a contractor\u2019s license and insurance?',
        a: 'CAFD does not vet, verify, or endorse contractors. Before hiring, ask each contractor for proof of a C-61/D-49 tree service contractor license and current general liability plus workers\u2019 compensation insurance. You can verify any contractor\u2019s CSLB license status for free at cslb.ca.gov.',
      },
    ],
  },

  brush_clearing: {
    title: 'Professional Brush Clearing & Weed Abatement',
    subtitle:
      'Keep your property fire-ready with expert brush clearing and vegetation management from local professionals.',
    colors: ['#4a5d1a', '#6d8a2d'],
    services: [
      {
        icon: '\u{1F33F}',
        title: 'Brush Clearing',
        description:
          'Remove dense brush, shrubs, and overgrown vegetation to create defensible space and reduce fire risk.',
      },
      {
        icon: '\u{1F33E}',
        title: 'Weed Abatement',
        description:
          'Control invasive weeds and dry grasses that serve as fire ladder fuels, meeting local abatement ordinances.',
      },
      {
        icon: '\u{1F9F5}',
        title: 'String Trimming',
        description:
          'Precision trimming along fences, foundations, pathways, and other areas that mowers cannot reach.',
      },
      {
        icon: '\u{1F331}',
        title: 'Undergrowth Removal',
        description:
          'Clear low-growing vegetation and leaf litter beneath tree canopies to eliminate ladder fuels.',
      },
      {
        icon: '\u{1F6E4}\uFE0F',
        title: 'Trail & Path Clearing',
        description:
          'Open and maintain access paths, hiking trails, and fire roads overgrown with brush.',
      },
      {
        icon: '\u{1F6A7}',
        title: 'Roadside & Fence Line Clearing',
        description:
          'Clear vegetation from road shoulders, fence lines, and property boundaries for access and fire safety.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Received a fire department abatement notice with deadline',
          'Brush is touching or overhanging structures',
          'Active fire season with dry, overgrown property',
          'Evacuation route blocked by overgrown vegetation',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Dry brush accumulating within 30 feet of structures',
          'Weeds exceeding local height ordinance limits',
          'Overgrowth blocking access to gates or driveways',
          'Property hasn\u2019t been cleared in over a year',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Seasonal spring clearing before fire season',
          'Annual defensible space maintenance schedule',
          'Pre-inspection clearing for insurance or compliance',
          'Community abatement deadlines approaching',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Estimate the area',
        description:
          'Measure or pace off the approximate square footage or acreage. Contractors price by area, so even a rough estimate speeds up quoting.',
      },
      {
        number: 2,
        title: 'Describe the terrain',
        description:
          'Is the land flat, gently sloped, or a steep hillside? Slopes affect equipment choices and pricing significantly.',
      },
      {
        number: 3,
        title: 'Identify vegetation types',
        description:
          'Note what\u2019s growing \u2014 dry grass, manzanita, poison oak, blackberry, etc. Some plants require special handling or disposal.',
      },
      {
        number: 4,
        title: 'Note access limitations',
        description:
          'Can a truck or mowing equipment access the area? Mention narrow roads, locked gates, steep driveways, or no-drive zones.',
      },
      {
        number: 5,
        title: 'Mark property boundaries',
        description:
          'Know where your property ends and your neighbor\u2019s begins. Flag or describe boundaries so contractors clear the right area.',
      },
      {
        number: 6,
        title: 'Specify disposal preferences',
        description:
          'Do you want brush chipped on-site, hauled off, or piled for later? Disposal method affects the quote significantly.',
      },
    ],
    faq: [
      {
        q: 'How much does brush clearing cost?',
        a: 'Cost depends mostly on acreage, slope, how dense the brush is, access for equipment, and whether material is chipped on-site or hauled away. Steep, brushy, hard-to-reach lots cost more. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'Do I need a permit for brush clearing?',
        a: 'Generally, no permit is required for vegetation management on your own property. However, some areas may have environmental restrictions, especially near waterways or protected habitats. Check with your local fire agency.',
      },
      {
        q: 'When is the best time to clear brush?',
        a: 'Late winter to early spring (February\u2013April) is ideal \u2014 vegetation is manageable and you\u2019re prepared before fire season. Fall clearing is also effective. Avoid clearing during nesting season (April\u2013August) when possible.',
      },
      {
        q: 'How often should I clear brush?',
        a: 'Most properties need annual clearing to stay compliant and fire-safe. Fast-growing areas or properties with heavy annual weed growth may benefit from twice-yearly clearing.',
      },
      {
        q: 'What happens to the brush after clearing?',
        a: 'Contractors can chip it on-site for mulch, haul it to a green waste facility, or pile it for a separate chipping service. Discuss options with your contractor to find the most cost-effective approach.',
      },
      {
        q: 'Can you clear brush on steep slopes?',
        a: 'Yes, many contractors specialize in steep terrain using hand crews, string trimmers, and specialized equipment. Steep slope work costs more due to safety considerations and slower progress.',
      },
      {
        q: 'Are there chemical-free clearing options?',
        a: 'Absolutely. Most fire mitigation brush clearing is mechanical \u2014 using mowers, trimmers, and hand tools. Goat grazing is another natural option. Chemical herbicides are rarely used for fire mitigation.',
      },
      {
        q: 'How does brush clearing help with defensible space?',
        a: 'Removing brush within 100 feet of structures is a core requirement of California\u2019s defensible space law (PRC 4291). Cleared land slows fire spread and gives firefighters room to work, significantly improving your home\u2019s chances in a wildfire.',
      },
    ],
  },

  // Merged category — combines the chipping + debris pages. Firewood is
  // deliberately soft-pedaled here; it gets its own category/page later.
  chipping_debris: {
    title: 'Professional Chipping & Debris Removal',
    subtitle:
      'One crew for the whole cleanup — chip brush and branches on-site, and haul away whatever’s left. Fast, efficient service from local professionals.',
    colors: ['#5d4a1a', '#8a6d2d'],
    services: [
      {
        icon: '\u{1FAB5}',
        title: 'On-Site Chipping',
        description:
          'A chipper comes to your property and processes brush and branches into chips right where they sit.',
      },
      {
        icon: '\u{1F69B}',
        title: 'Green Waste Hauling',
        description:
          'Pickup and disposal of brush, branches, leaves, and other organic material to certified facilities.',
      },
      {
        icon: '\u{1F4E6}',
        title: 'Brush Pile Chipping & Removal',
        description:
          'Convert accumulated brush piles into chips on-site — or have them hauled away entirely for a clean slate.',
      },
      {
        icon: '⚡',
        title: 'Storm Debris Cleanup',
        description:
          'Rapid response cleanup of fallen limbs, broken branches, and storm damage debris.',
      },
      {
        icon: '\u{1F525}',
        title: 'Fire Fuel Reduction',
        description:
          'Chip or remove vegetation cleared for defensible space compliance, reducing bulk and eliminating fire fuel.',
      },
      {
        icon: '\u{1F69A}',
        title: 'Chip Spreading & Delivery',
        description:
          'Spread chips as mulch on-site or deliver to a designated location for landscaping use.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Fire season deadline approaching with large piles on property',
          'Storm debris blocking driveway or road access',
          'Fire department notice citing accumulated debris',
          'Brush or debris near structures creating immediate fire hazard',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Large brush piles from recent tree or clearing work',
          'Piles drying out and becoming more flammable',
          'Property cleanup ahead of fire inspection',
          'Neighbors or HOA requesting pile removal',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Upcoming community chipping event registration',
          'Scheduling cleanup after planned tree or brush work',
          'Pre-fire-season property cleanup',
          'Seasonal leaf and needle cleanup hauling',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Estimate the volume',
        description:
          'Measure your piles’ approximate length, width, and height — or think in truckloads (a standard dump truck holds about 10 cubic yards). Contractors quote by volume or time.',
      },
      {
        number: 2,
        title: 'Remove metal and wire',
        description:
          'Pull out any fencing wire, nails, rebar, or other metal from your piles. Metal can damage chipper blades and create safety hazards.',
      },
      {
        number: 3,
        title: 'Decide: chips or clean slate?',
        description:
          'Chipping reduces volume by ~75% and makes free mulch. Hauling leaves a completely clean site. Many jobs combine both — tell contractors what outcome you want.',
      },
      {
        number: 4,
        title: 'Confirm equipment access',
        description:
          'Chippers and dump trucks need access within a reasonable distance of the piles. Note road widths, gate clearances, unpaved driveways, and obstacles.',
      },
      {
        number: 5,
        title: 'Describe the material',
        description:
          'Note the species (pine, oak, manzanita, etc.), whether it’s green or dry, and any unusually heavy items like stumps or logs.',
      },
      {
        number: 6,
        title: 'Specify what to keep',
        description:
          'Want to keep firewood, usable lumber, or chips as mulch? Tell contractors upfront what to process, what to haul, and what to leave.',
      },
    ],
    faq: [
      {
        q: 'Is chipping better than hauling?',
        a: 'Chipping reduces volume by about 75% and creates useful mulch, making it more environmentally friendly and often cheaper than hauling. Hauling is better for contaminated material or when you need a completely clean site. Many jobs combine both — chip what’s chippable, haul the rest.',
      },
      {
        q: 'How much does chipping cost?',
        a: 'Cost depends mostly on how many piles there are and how big, what\u2019s in them (branches vs. logs), and how close the chipper can get. Some pros charge by the hour and some by the pile or volume. Including pile dimensions and photos helps. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'How much does debris hauling cost?',
        a: 'Cost depends mostly on the amount and type of material, how far it has to go to a disposal site, and how hard it is to load. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'What can’t be chipped?',
        a: 'Chippers cannot process rocks, dirt, metal, roots with soil, or material larger than the chipper’s rated capacity (usually 6–8 inches for residential equipment). That material gets hauled instead — one more reason a combined crew is convenient.',
      },
      {
        q: 'Do contractors load the debris, or do I need to?',
        a: 'Most contractors include loading in their price. If material is already loosely piled near the access point with butt-ends facing the same direction, the job goes faster and costs less.',
      },
      {
        q: 'Can chips be used as mulch?',
        a: 'Yes! Fresh wood chips make excellent mulch for garden beds, pathways, and erosion control. However, keep chips at least 5 feet from structures for fire safety. Your contractor can spread chips on-site.',
      },
      {
        q: 'Are there community chipping programs available?',
        a: 'Many California communities offer free or subsidized chipping through local Fire Safe Councils, especially before fire season. Check with your local fire department or county for programs in your area.',
      },
      {
        q: 'What are California’s green waste regulations?',
        a: 'California law (SB 1383) requires organic waste diversion from landfills. Green waste must go to composting or chipping facilities. Reputable contractors use certified disposal sites that comply with these regulations.',
      },
    ],
  },

  dsc: {
    title: 'Professional Defensible Space Clearing',
    subtitle:
      'Create the required defensible space around your home with expert vegetation management from local professionals.',
    colors: ['#1a5d4a', '#2d8a6e'],
    services: [
      {
        icon: '\u{1F3E0}',
        title: 'Zone 0: Ember-Resistant Zone (0\u20135 ft)',
        description:
          'Remove all combustible material immediately adjacent to structures \u2014 the most critical zone for home survival.',
      },
      {
        icon: '\u{1F3E1}',
        title: 'Zone 1: Lean, Clean & Green (5\u201330 ft)',
        description:
          'Create a well-maintained landscape with fire-resistant plants, proper spacing, and no ladder fuels.',
      },
      {
        icon: '\u{1F332}',
        title: 'Zone 2: Reduced Fuel Zone (30\u2013100 ft)',
        description:
          'Thin trees, reduce brush density, and create horizontal spacing to slow wildfire spread toward your home.',
      },
      {
        icon: '\u{1F6AB}',
        title: 'Dead Tree & Vegetation Removal',
        description:
          'Remove all dead trees, dead branches, dry brush, and leaf litter that serve as wildfire fuel.',
      },
      {
        icon: '\u2B06\uFE0F',
        title: 'Ladder Fuel Elimination',
        description:
          'Remove vegetation that allows fire to climb from the ground into tree canopies \u2014 a critical fire safety step.',
      },
      {
        icon: '\u{1F6E1}\uFE0F',
        title: 'Fire Break Creation',
        description:
          'Establish cleared buffers along property edges and between structures to interrupt fire paths.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Received a CAL FIRE or fire department citation',
          'Abatement notice with an upcoming deadline',
          'Insurance company requiring immediate compliance',
          'Active fire in your region with no defensible space',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Property has never had defensible space clearing',
          'More than two years since last clearing',
          'Significant dead vegetation accumulating',
          'Fire inspection scheduled in the coming weeks',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Annual pre-fire-season clearing (Feb\u2013April)',
          'New home purchase in a fire-prone area',
          'Scheduling ongoing maintenance program',
          'Community-wide fire safe council efforts',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Know your zones',
        description:
          'Defensible space is described in three zones: Zone 0 (0\u20135 ft), Zone 1 (5\u201330 ft), and Zone 2 (30\u2013100 ft). Where state defensible space law applies, it covers the full 100 feet, including some upkeep right next to the house such as roof debris and chimney clearance. California\u2019s Board of Forestry adopted a statewide rule adding stricter Zone 0 standards in August 2026. It is not yet in effect and has no effective date. Understanding the zones helps you describe what\u2019s needed.',
      },
      {
        number: 2,
        title: 'Document all structures',
        description:
          'Include your house, garage, shed, deck, and any outbuildings. Each structure needs its own defensible space buffer.',
      },
      {
        number: 3,
        title: 'Measure your property',
        description:
          'Know the approximate lot size and distance from structures to property boundaries. This determines how much of each zone can be addressed on your land.',
      },
      {
        number: 4,
        title: 'Photograph current conditions',
        description:
          'Take wide-angle photos from each side of your home showing the surrounding vegetation. Include close-ups of any problem areas.',
      },
      {
        number: 5,
        title: 'Check local requirements',
        description:
          'Some counties or fire districts have additional requirements beyond PRC 4291. Mention your jurisdiction so pros can plan for local requirements.',
      },
      {
        number: 6,
        title: 'Gather previous reports',
        description:
          'If you have a previous fire inspection report, insurance assessment, or clearance record, share it with contractors so they can focus on what\u2019s needed.',
      },
    ],
    faq: [
      {
        q: 'How much does defensible space clearing cost?',
        a: 'Cost depends mostly on lot size, slope, how overgrown the property is, and access for crews and equipment. Larger or heavily overgrown properties cost more. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'What does California law require?',
        a: 'PRC 4291 requires 100 feet of defensible space around structures in the State Responsibility Area (SRA) and high fire hazard zones. That includes some upkeep right next to the house, such as keeping the roof clear of leaves and needles and removing tree branches within 10 feet of a chimney. California\u2019s Board of Forestry adopted a statewide rule adding stricter standards for Zone 0 (ember-resistant, 0\u20135 ft) in August 2026. It is not yet in effect and has no effective date. Some cities and counties already require it. Check with your local fire agency.',
      },
      {
        q: 'What are the defensible space zones?',
        a: 'Zone 0 (0\u20135 ft): Keep it free of combustible material (recommended; a statewide rule adopted in August 2026 is not yet in effect and has no effective date). Zone 1 (5\u201330 ft): Fire-resistant landscaping, well-spaced plants, no ladder fuels. Zone 2 (30\u2013100 ft): Reduced fuel with horizontal spacing between shrubs and trees. Which practices are legally required depends on where you live.',
      },
      {
        q: 'How do fire inspections work?',
        a: 'CAL FIRE or your local fire department may inspect properties annually before fire season. They check your property against the rules that apply in your area and issue a pass, advisory, or citation. Fixing issues before inspection avoids fines and potential liens.',
      },
      {
        q: 'What are the penalties for non-compliance?',
        a: 'Penalties vary by jurisdiction and can include citations and fines, administrative fees, and property liens. In some areas the fire agency may clear the property and bill the owner. Insurance companies may also raise premiums or decline to renew coverage.',
      },
      {
        q: 'Does defensible space affect my insurance?',
        a: 'Yes, significantly. Many California insurers now inspect defensible space before issuing or renewing policies in fire-prone areas. Proper defensible space can help you maintain coverage and may qualify you for lower premiums.',
      },
      {
        q: 'How often should I have defensible space cleared?',
        a: 'An initial clearing followed by annual maintenance is recommended. Vegetation regrows, leaves accumulate, and plants die \u2014 all requiring ongoing attention to maintain compliance.',
      },
      {
        q: 'What vegetation stays and what goes?',
        a: 'Healthy, fire-resistant plants with proper spacing can stay. Dead plants, dry brush, leaf litter, ladder fuels, and highly flammable species (juniper, cypress, pampas grass) should be removed or replaced. Your contractor will advise on what to keep.',
      },
    ],
  },

  frl: {
    title: 'Fire-Resistant Landscaping Design & Installation',
    subtitle:
      'Protect your home and beautify your property with fire-smart landscaping designed by local professionals.',
    colors: ['#2a5d1a', '#3d8a2d'],
    services: [
      {
        icon: '\u{1F33A}',
        title: 'Fire-Resistant Plant Design',
        description:
          'Professionally designed planting plans using fire-smart species with proper spacing for your specific property.',
      },
      {
        icon: '\u{1FAA8}',
        title: 'Hardscaping & Stone Work',
        description:
          'Install gravel paths, stone walls, patios, and other non-combustible features that double as fire breaks.',
      },
      {
        icon: '\u{1F4A7}',
        title: 'Irrigation Installation',
        description:
          'Drip and spray irrigation systems designed to keep fire-resistant plants healthy while conserving water.',
      },
      {
        icon: '\u{1F33F}',
        title: 'Native Plant Landscaping',
        description:
          'Use California native plants that are naturally adapted to local fire cycles and require less water.',
      },
      {
        icon: '\u{1F33E}',
        title: 'Ground Cover & Mulch',
        description:
          'Select the right ground covers and non-combustible mulch to suppress weeds and maintain moisture.',
      },
      {
        icon: '\u{1F504}',
        title: 'Landscape Renovation',
        description:
          'Retrofit existing landscaping by replacing flammable plants and adding fire-resistant alternatives.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Rebuilding landscaping after wildfire damage',
          'Insurance requiring fire-resistant landscaping compliance',
          'Newly built home needing landscape before fire season',
          'Cited for flammable vegetation too close to structures',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Existing landscape heavily planted with flammable species',
          'Planning a home remodel that includes outdoor spaces',
          'Wanting to reduce water usage with drought-tolerant plants',
          'Neighbors have cleared but your landscaping is still a risk',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Fall or spring planting window for best plant establishment',
          'Designing landscape for a new home or addition',
          'Phased landscape renovation over multiple seasons',
          'Community fire-safe landscaping improvement program',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Know your climate zone',
        description:
          'California spans many microclimates. Share your USDA zone or city so contractors can recommend plants that thrive in your specific conditions.',
      },
      {
        number: 2,
        title: 'Measure the area',
        description:
          'Sketch or measure the space you want landscaped. Include dimensions of beds, paths, and areas around structures for accurate design quotes.',
      },
      {
        number: 3,
        title: 'Note sun and shade patterns',
        description:
          'Which areas get full sun, partial shade, or deep shade? Plant selection depends heavily on light exposure throughout the day.',
      },
      {
        number: 4,
        title: 'Describe existing landscaping',
        description:
          'What\u2019s already planted? What do you want to keep? What needs replacing? Photos of existing conditions help designers plan efficiently.',
      },
      {
        number: 5,
        title: 'Set a budget range',
        description:
          'Landscaping costs vary widely. Sharing a budget range helps contractors design within your means and suggest phased approaches if needed.',
      },
      {
        number: 6,
        title: 'Share inspiration photos',
        description:
          'Save photos of landscapes you admire. This helps designers understand your aesthetic preferences alongside fire safety requirements.',
      },
    ],
    faq: [
      {
        q: 'How much does fire-resistant landscaping cost?',
        a: 'Cost depends mostly on scope: a small update right around the house costs far less than a full landscape redesign. Materials (gravel, pavers, plants) and removal of existing mulch or plants also matter. Many homeowners phase the work over several seasons. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'What are the best fire-resistant plants?',
        a: 'Excellent choices include California fuchsia, ceanothus, manzanita (low-growing varieties), lavender, rosemary, sage, California poppy, and native bunch grasses. Avoid highly flammable plants like juniper, bamboo, pampas grass, and Italian cypress.',
      },
      {
        q: 'Does fire-resistant landscaping really help?',
        a: 'Yes, significantly. Research shows that properly landscaped defensible space is one of the most effective factors in home survival during wildfire. It reduces radiant heat, eliminates fuel near structures, and gives firefighters room to work.',
      },
      {
        q: 'Will it use a lot of water?',
        a: 'Fire-resistant landscaping is typically drought-tolerant once established. Native and Mediterranean plants require 50\u201370% less water than traditional lawns. Efficient drip irrigation and mulch further reduce water needs.',
      },
      {
        q: 'How much maintenance is required?',
        a: 'Less than traditional landscaping in most cases. Fire-resistant plants are selected for low maintenance. Plan on seasonal pruning, occasional dead-heading, and keeping areas free of dry leaf litter.',
      },
      {
        q: 'Can fire-resistant landscaping look attractive?',
        a: 'Absolutely. Fire-smart landscapes can be stunning, with colorful flowering plants, interesting textures, and beautiful hardscaping. Many award-winning California gardens are designed with fire safety in mind.',
      },
      {
        q: 'Will fire-resistant landscaping lower my insurance premiums?',
        a: 'Many insurers in California now factor defensible space and landscaping into their risk assessments. While discounts vary, proper fire-resistant landscaping can help you maintain coverage and may qualify for premium reductions.',
      },
      {
        q: 'Should I use native or non-native plants?',
        a: 'A mix often works best. California natives are naturally fire-adapted and drought-tolerant, but some non-native Mediterranean plants are also excellent fire-resistant choices. Your designer can recommend the best mix for your site.',
      },
    ],
  },

  grazing: {
    title: 'Professional Grazing Services for Vegetation Management',
    subtitle:
      'Harness the power of goats and sheep for natural, eco-friendly brush and weed control on your property.',
    colors: ['#3d5d2a', '#5e8a3d'],
    services: [
      {
        icon: '\u{1F410}',
        title: 'Targeted Goat Grazing',
        description:
          'Deploy managed goat herds to eat brush, weeds, and invasive plants \u2014 perfect for steep or sensitive terrain.',
      },
      {
        icon: '\u{1F411}',
        title: 'Sheep Grazing',
        description:
          'Use sheep herds for grass and weed control on flatter terrain, pastures, and open fields.',
      },
      {
        icon: '\u26F0\uFE0F',
        title: 'Steep Slope Vegetation Control',
        description:
          'Goats excel on terrain too steep or rocky for machinery, safely clearing slopes that other methods can\u2019t reach.',
      },
      {
        icon: '\u{1F3DE}\uFE0F',
        title: 'Large Parcel Management',
        description:
          'Cost-effective vegetation management for large properties, ranches, and open spaces using rotational grazing.',
      },
      {
        icon: '\u{1F33F}',
        title: 'Invasive Species Targeting',
        description:
          'Goats and sheep can target specific invasive plants like star thistle, blackberry, poison oak, and mustard.',
      },
      {
        icon: '\u{1F91D}',
        title: 'Community Grazing Programs',
        description:
          'Organize neighborhood or community grazing events to clear shared spaces, hillsides, and open areas.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Grazing is typically not an emergency service',
          'If you need urgent clearing, consider brush clearing services',
          'Grazing requires planning and takes days to weeks',
          'For fire emergencies, contact your local fire department',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Fire season approaching and vegetation is high',
          'Weeds and brush growing rapidly after rains',
          'Community fire safety event being planned',
          'Insurance requiring vegetation management action',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Book early \u2014 spring and summer fill up fast',
          'Schedule for when vegetation is green and palatable',
          'Coordinate with neighbors for group pricing',
          'Plan as part of an annual vegetation management cycle',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Measure your acreage',
        description:
          'Grazing is priced per acre. Knowing your exact or approximate acreage is essential for accurate quotes from grazing providers.',
      },
      {
        number: 2,
        title: 'Describe terrain and slope',
        description:
          'Is the land flat, rolling, or steep? Goats handle steep terrain well, while sheep prefer gentler slopes. Terrain affects herd size and duration.',
      },
      {
        number: 3,
        title: 'Note existing fencing',
        description:
          'Do you have perimeter fencing? If not, providers bring temporary electric fencing. Existing fencing may reduce setup costs.',
      },
      {
        number: 4,
        title: 'Identify plants to protect',
        description:
          'Goats eat almost everything. If you have gardens, fruit trees, or ornamentals you want protected, they\u2019ll need to be fenced off.',
      },
      {
        number: 5,
        title: 'Confirm water source access',
        description:
          'Animals need water daily. Let contractors know if you have a water source on-site or if they need to bring water tanks.',
      },
      {
        number: 6,
        title: 'Discuss timeline flexibility',
        description:
          'Grazing takes time \u2014 typically 3\u20147 days per acre depending on vegetation density. Flexible scheduling helps providers work efficiently.',
      },
    ],
    faq: [
      {
        q: 'How much does grazing cost?',
        a: 'Cost depends mostly on acreage, how dense the vegetation is, terrain, fencing, water access, and how long the animals need to stay. Some community programs offer subsidized grazing. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'How long does grazing take?',
        a: 'A herd of 100\u2013200 goats can clear about 1 acre every 3\u20147 days, depending on vegetation density and type. Your provider will estimate the timeline based on your specific property.',
      },
      {
        q: 'Goats or sheep \u2014 which is better?',
        a: 'Goats are better for brush, woody plants, and steep terrain \u2014 they prefer to browse. Sheep are better for grass and flat to rolling terrain \u2014 they prefer to graze. Many providers will recommend the right animals for your situation.',
      },
      {
        q: 'Do I need to install fencing?',
        a: 'No. Grazing providers bring their own temporary electric fencing to contain the herd and direct them to target areas. A herder or guard dog typically accompanies the animals 24/7.',
      },
      {
        q: 'Will goats eat my garden or landscaping?',
        a: 'Goats will eat almost any plant within reach. Sensitive areas like gardens, ornamental plantings, and young trees need to be fenced off separately. Discuss protected zones with your provider.',
      },
      {
        q: 'What about noise and smell?',
        a: 'Goats and sheep are quieter than you\u2019d expect. There will be some bleating, especially at feeding time. Odor is minimal for short-term grazing. Most neighbors find the animals charming rather than bothersome.',
      },
      {
        q: 'Are there permitting requirements?',
        a: 'Requirements vary by jurisdiction. Some areas require a temporary use permit for commercial grazing. Your provider should be familiar with local regulations and handle any necessary permits.',
      },
      {
        q: 'How does grazing compare to mechanical clearing?',
        a: 'Grazing is gentler on the soil, reduces erosion on slopes, naturally fertilizes the land, and avoids the noise and emissions of machinery. It\u2019s slower than mechanical clearing but ideal for steep, sensitive, or difficult-to-access terrain.',
      },
    ],
  },

  hh: {
    title: 'Professional Home Hardening for Wildfire Protection',
    subtitle:
      'Upgrade your home\u2019s defenses against wildfire with fire-resistant retrofits from licensed professionals.',
    resourceLink: {
      label: 'CAL FIRE Home Hardening Guide',
      url: 'https://www.readyforwildfire.org/prepare-for-wildfire/hardening-your-home',
    },
    colors: ['#3d4a5d', '#5a6d8a'],
    services: [
      {
        icon: '\u{1F3E0}',
        title: 'Ember-Resistant Vent Screening',
        description:
          'Replace standard vents with 1/8-inch mesh screens that block embers \u2014 one of the top causes of home ignition.',
      },
      {
        icon: '\u{1F3DA}\uFE0F',
        title: 'Roof & Gutter Upgrades',
        description:
          'Install Class A fire-rated roofing and gutter guards to prevent ember accumulation in your roof system.',
      },
      {
        icon: '\u{1F9F1}',
        title: 'Fire-Resistant Siding & Decking',
        description:
          'Replace combustible wood siding and decking with fiber cement, metal, or composite fire-resistant alternatives.',
      },
      {
        icon: '\u{1FA9F}',
        title: 'Window & Door Retrofits',
        description:
          'Install dual-pane tempered glass windows and fire-rated doors to resist radiant heat and ember intrusion.',
      },
      {
        icon: '\u{1F527}',
        title: 'Under-Eave & Soffit Protection',
        description:
          'Enclose open eaves and install fire-resistant soffits to prevent embers from entering the attic space.',
      },
      {
        icon: '\u{1F6E1}\uFE0F',
        title: 'Exterior Coating & Sealants',
        description:
          'Apply fire-retardant coatings and seal gaps where embers could penetrate walls, foundations, and junctions.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Home in an active fire evacuation warning zone',
          'Insurance threatening non-renewal due to fire risk',
          'Visible structural vulnerabilities (open eaves, wood vents)',
          'Recent wildfire damage needing immediate repair',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Older home with original wood siding or shake roof',
          'Located in a designated WUI (Wildland-Urban Interface) zone',
          'Insurance inspection scheduled in coming weeks',
          'Planning renovations that could include fire upgrades',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Incorporating fire resistance into a planned remodel',
          'Budgeting for phased home hardening improvements',
          'New construction planning with fire-resistant materials',
          'Applying for home hardening grants or subsidies',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Document current materials',
        description:
          'Note your roof type, siding material, vent types, window glazing, and deck construction. Photos of each element help contractors assess your needs.',
      },
      {
        number: 2,
        title: 'Note home age and construction',
        description:
          'Older homes typically have more vulnerabilities. Share your home\u2019s approximate age and construction type (wood frame, stucco, etc.).',
      },
      {
        number: 3,
        title: 'Prioritize by vulnerability',
        description:
          'Vents and roofing are typically the #1 priority, followed by eaves, then siding and decking. Contractors can help you prioritize based on your home\u2019s specific weak points.',
      },
      {
        number: 4,
        title: 'Check insurance requirements',
        description:
          'Some insurers specify exact upgrades needed for coverage. Share any insurance correspondence so contractors can address those requirements directly.',
      },
      {
        number: 5,
        title: 'Get multiple quotes',
        description:
          'Home hardening involves specialized work. Getting 2\u20133 quotes ensures fair pricing and helps you understand the range of approaches.',
      },
      {
        number: 6,
        title: 'Ask about financing programs',
        description:
          'California offers grants and low-interest loans for home hardening. Ask contractors about CAL FIRE programs, FEMA hazard mitigation grants, and local fire-safe council resources.',
      },
    ],
    faq: [
      {
        q: 'How much does home hardening cost?',
        a: 'Cost depends on which upgrades you choose and the size of the home. Smaller jobs like vent screens and gutter guards cost far less than replacing siding, windows, or a roof. Many homeowners start with the smaller upgrades and plan larger ones over time. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'What upgrades have the most impact?',
        a: 'Research consistently shows that ember-resistant vents, Class A roofing, and enclosed eaves provide the greatest fire protection per dollar. These three upgrades address the most common ignition points during wildfire.',
      },
      {
        q: 'Will home hardening lower my insurance premiums?',
        a: 'Many California insurers now offer premium reductions for verified home hardening improvements. Some require specific upgrades for continued coverage in high-risk areas. Document all improvements for your insurer.',
      },
      {
        q: 'Do I need permits for home hardening work?',
        a: 'Some upgrades require permits (window replacement, siding, roofing), while others don\u2019t (vent screening, gutter guards). Your contractor should handle permitting. Check with your local building department for specifics.',
      },
      {
        q: 'How long does home hardening take?',
        a: 'Simple upgrades like vent screening can be done in a day. Roof replacement takes 1\u20133 days. Comprehensive siding and window upgrades may take 1\u20132 weeks. Your contractor will provide a timeline based on the scope.',
      },
      {
        q: 'What are WUI building requirements?',
        a: 'Homes in the Wildland-Urban Interface (WUI) must meet California Building Code Chapter 7A, which specifies fire-resistant requirements for roofing, exterior walls, windows, eaves, decks, and vents. New construction must comply; existing homes are encouraged to retrofit.',
      },
      {
        q: 'What are ember-resistant vents?',
        a: 'Ember-resistant vents use 1/8-inch corrosion-resistant mesh (or intumescent vents that swell shut in heat) to prevent wind-blown embers from entering your attic, crawl space, or eaves. They\u2019re one of the most cost-effective fire upgrades.',
      },
      {
        q: 'Is it worth hardening an existing home vs. rebuilding?',
        a: 'In most cases, retrofitting an existing home is far more cost-effective than rebuilding. Even partial hardening significantly improves your home\u2019s survival chances. Focus on the highest-impact upgrades first and phase additional work over time.',
      },
    ],
  },

  heavy_machinery: {
    title: 'Heavy Machinery & Land Clearing Services',
    subtitle:
      'Tackle large-scale clearing projects with professional operators and the right equipment for any terrain.',
    colors: ['#4a3d2a', '#6d5a3d'],
    services: [
      {
        icon: '\u{1F69C}',
        title: 'Excavator Services',
        description:
          'Precision tree and stump removal, grading, and terrain shaping with hydraulic excavators.',
      },
      {
        icon: '\u{1F6A7}',
        title: 'Bulldozer Clearing',
        description:
          'Large-scale brush pushing, road building, and terrain leveling for fire breaks and land development.',
      },
      {
        icon: '\u2699\uFE0F',
        title: 'Masticator & Forestry Mulching',
        description:
          'Grind standing trees and brush into mulch in place \u2014 no hauling required. Ideal for fire fuel reduction.',
      },
      {
        icon: '\u{1F4CF}',
        title: 'Grading & Road Building',
        description:
          'Build and maintain fire access roads, driveways, and graded pads for structures.',
      },
      {
        icon: '\u{1F6E1}\uFE0F',
        title: 'Firebreak Construction',
        description:
          'Create wide, bare-soil fire breaks to protect communities, properties, and critical infrastructure.',
      },
      {
        icon: '\u{1F332}',
        title: 'Large-Scale Vegetation Removal',
        description:
          'Clear multiple acres of dense forest, brush, or overgrown land efficiently with heavy equipment.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Emergency firebreak needed due to approaching wildfire',
          'Storm damage requiring heavy equipment for road clearing',
          'Large fallen trees blocking critical access roads',
          'Post-fire debris removal and site stabilization',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Pre-fire-season large-scale vegetation reduction',
          'Community firebreak maintenance before dry conditions',
          'Land clearing for permitted construction project',
          'Fire road repairs needed before they become impassable',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Land development and site preparation projects',
          'Annual fire road grading and maintenance schedule',
          'Large parcel vegetation management planning',
          'Community fuel reduction grant-funded projects',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Know your acreage',
        description:
          'Heavy machinery work is priced by the acre or by the hour. Knowing the exact area to be cleared is essential for accurate bidding.',
      },
      {
        number: 2,
        title: 'Describe terrain and slope grade',
        description:
          'Flat, moderate (15\u201330%), or steep (30%+)? Slope grade determines which equipment can safely operate on your property.',
      },
      {
        number: 3,
        title: 'Specify tree sizes and density',
        description:
          'How many trees per acre? What\u2019s the average diameter? Dense forests with large trees require different equipment than sparse brush.',
      },
      {
        number: 4,
        title: 'Note property boundaries',
        description:
          'Flag or describe boundaries clearly. Heavy equipment moves fast \u2014 operators need clear limits to avoid clearing neighboring land.',
      },
      {
        number: 5,
        title: 'Locate underground utilities',
        description:
          'Call 811 before any heavy equipment work. Note locations of septic systems, water lines, gas lines, and electrical conduits.',
      },
      {
        number: 6,
        title: 'Confirm equipment access routes',
        description:
          'How does heavy equipment reach the site? Note road widths, weight-limited bridges, locked gates, and any access restrictions.',
      },
    ],
    faq: [
      {
        q: 'How much does heavy equipment land clearing cost?',
        a: 'Cost depends mostly on acreage, vegetation density, slope, tree sizes, and which equipment the job needs. Some pros charge by the acre and some by the hour. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'Which equipment is right for my project?',
        a: 'Masticators grind vegetation in place (ideal for fuel reduction). Excavators handle tree removal and grading. Bulldozers are best for road building and large-scale pushing. Your contractor will recommend the right equipment based on your goals.',
      },
      {
        q: 'Do I need permits for heavy machinery work?',
        a: 'Many jurisdictions require grading permits, timber harvest plans, or environmental review for large-scale clearing. Your contractor should know local requirements. Always check with your county planning department before starting.',
      },
      {
        q: 'What about environmental considerations?',
        a: 'Reputable operators follow best management practices (BMPs) for erosion control, stream protection, and wildlife habitat. Work near waterways may require additional permits. Discuss environmental concerns with your contractor upfront.',
      },
      {
        q: 'How long does clearing a large parcel take?',
        a: 'A masticator can typically clear 1\u20133 acres per day of moderate vegetation. A bulldozer can push 2\u20135 acres per day. Dense forest with large trees is slower. Your contractor will estimate based on site conditions.',
      },
      {
        q: 'What about site restoration after clearing?',
        a: 'Heavy equipment can disturb soil and leave a rough finish. Many contractors offer finish grading, erosion control (seeding, mulching, wattles), and road surfacing as part of the project or as add-on services.',
      },
      {
        q: 'What\u2019s the difference between masticating and traditional clearing?',
        a: 'Masticating grinds everything in place \u2014 no piles, no hauling, and the mulch helps prevent erosion. Traditional clearing (cut-and-haul) removes material from the site. Masticating is often faster and cheaper for fire fuel reduction.',
      },
      {
        q: 'Are there noise or operating hour restrictions?',
        a: 'Most counties have noise ordinances limiting heavy equipment operation to daytime hours (typically 7 AM\u20137 PM). Some residential areas have stricter limits. Check with your local code enforcement.',
      },
    ],
  },

  consultation: {
    title: 'Fire Safety Consultation & Property Assessments',
    subtitle:
      'Get expert guidance on fire risk, defensible space compliance, and vegetation management planning.',
    colors: ['#1a3d5d', '#2d5e8a'],
    services: [
      {
        icon: '\u{1F4CB}',
        title: 'Fire Risk Assessment',
        description:
          'Comprehensive evaluation of your property\u2019s wildfire risk factors, vulnerabilities, and recommended actions.',
      },
      {
        icon: '\u{1F50D}',
        title: 'Defensible Space Evaluation',
        description:
          'Zone-by-zone review of your defensible space compliance with specific recommendations for each area.',
      },
      {
        icon: '\u{1F4DD}',
        title: 'Vegetation Management Plan',
        description:
          'A written plan outlining what to clear, what to keep, and a timeline for bringing your property into compliance.',
      },
      {
        icon: '\u{1F4C4}',
        title: 'Insurance Compliance Review',
        description:
          'Assessment aligned with insurance company requirements to help maintain or obtain wildfire coverage.',
      },
      {
        icon: '\u{1F527}',
        title: 'Post-Fire Recovery Planning',
        description:
          'Expert guidance on restoration priorities, erosion control, replanting, and rebuilding after wildfire.',
      },
      {
        icon: '\u{1F46B}',
        title: 'Community Wildfire Safety Assessment',
        description:
          'Multi-property or neighborhood assessments for Fire Safe Councils and community wildfire protection plans.',
      },
    ],
    urgency: [
      {
        level: 'Emergency',
        color: 'red',
        items: [
          'Insurance company requiring assessment for policy renewal',
          'Received a compliance notice with a deadline',
          'Recently purchased property in a fire zone \u2014 need immediate guidance',
          'Post-fire assessment needed for recovery planning',
        ],
      },
      {
        level: 'Soon',
        color: 'orange',
        items: [
          'Buying property in a fire-prone area and need risk evaluation',
          'Recent wildfire nearby \u2014 want to understand your risk',
          'Insurance premium increase \u2014 seeking ways to reduce risk',
          'Planning major fire mitigation work and want expert input',
        ],
      },
      {
        level: 'Plan Ahead',
        color: 'green',
        items: [
          'Annual property assessment before fire season',
          'Community FireWise certification process',
          'Long-term vegetation management planning',
          'Proactive risk reduction for peace of mind',
        ],
      },
    ],
    tips: [
      {
        number: 1,
        title: 'Prepare property details',
        description:
          'Know your lot size, home age, roof type, and surrounding terrain. The more details you provide upfront, the more targeted the consultation will be.',
      },
      {
        number: 2,
        title: 'List your specific concerns',
        description:
          'Are you worried about insurance, compliance, a specific area of your property, or general fire readiness? Clear goals help the consultant focus.',
      },
      {
        number: 3,
        title: 'Gather insurance documents',
        description:
          'If insurance is a factor, have your current policy, any non-renewal notices, or inspection reports ready to share with the consultant.',
      },
      {
        number: 4,
        title: 'Note previous work done',
        description:
          'Share what fire mitigation work has already been completed and when. This helps consultants build on existing efforts rather than starting from scratch.',
      },
      {
        number: 5,
        title: 'Ask about certifications',
        description:
          'Look for consultants with ISA (arborist), NFPA Firewise, or CAL FIRE-recognized certifications. Certified assessments carry more weight with insurers.',
      },
      {
        number: 6,
        title: 'Clarify the scope of the report',
        description:
          'Do you need a simple verbal walkthrough, a written report with photos, or a formal assessment for insurance? The deliverable affects pricing.',
      },
    ],
    faq: [
      {
        q: 'How much does a fire consultation cost?',
        a: 'Cost depends mostly on what you need: a walk-through with verbal advice costs less than a detailed written report, and insurance-specific assessments may cost more. Some local programs offer free or subsidized assessments. Describing the job once, with photos, and then comparing quotes from more than one local pro is the best way to see the range for your property.',
      },
      {
        q: 'What\u2019s included in a fire risk assessment?',
        a: 'A typical assessment covers defensible space compliance, home vulnerability analysis, vegetation mapping, access and egress review, water supply evaluation, and prioritized recommendations. The deliverable ranges from a verbal walkthrough to a detailed written report.',
      },
      {
        q: 'Is a professional consultation worth the cost?',
        a: 'Yes, especially if you\u2019re unsure where to start. A professional assessment identifies the most critical improvements, helping you spend your fire mitigation budget effectively. It can also provide documentation that satisfies insurance requirements.',
      },
      {
        q: 'Can a consultation help with insurance?',
        a: 'Absolutely. Many insurers accept professional fire risk assessments as evidence of mitigation efforts. A written report documenting your home\u2019s fire resistance and defensible space can support policy applications, renewals, and premium negotiations.',
      },
      {
        q: 'How long does an assessment take?',
        a: 'An on-site property assessment typically takes 1\u20132 hours for a standard residential property. The written report follows within 1\u20132 weeks. Larger properties or community assessments take proportionally longer.',
      },
      {
        q: 'Who performs fire risk assessments?',
        a: 'Assessments are performed by certified arborists, fire safety consultants, retired fire marshals, and vegetation management specialists. Look for ISA certification, NFPA Firewise training, or CAL FIRE recognition.',
      },
      {
        q: 'What certifications should I look for?',
        a: 'ISA Certified Arborist, NFPA Firewise USA, Certified Fire Mitigation Specialist, and CAL FIRE-recognized assessors are all strong credentials. Some insurers have specific assessor requirements \u2014 check with yours.',
      },
      {
        q: 'Will I receive a written report?',
        a: 'Most consultants offer tiered services: a verbal walkthrough with notes, a standard written report with photos and recommendations, or a formal assessment document suitable for insurance submission. Clarify which format you need when requesting quotes.',
      },
    ],
  },
};

/**
 * Maps each Console category ID to its info page URL path.
 * Used by SectionCarousel and RequestIntakePage to route category clicks.
 */
export const CATEGORY_INFO_ROUTES = {
  tree_service: '/services/tree-trimming-removal',
  brush_clearing: '/services/brush-clearing',
  chipping: '/services/chipping-debris-removal',
  debris: '/services/chipping-debris-removal',
  chipping_debris: '/services/chipping-debris-removal',
  dsc: '/services/defensible-space-clearing',
  frl: '/services/fire-resistant-landscaping',
  grazing: '/services/grazing',
  hh: '/services/home-hardening',
  heavy_machinery: '/services/heavy-machinery',
  consultation: '/services/consultation',
};
